import { CompoundButton, Button, Spinner, Text } from "@fluentui/react-components";
import { ContractAddendum } from "../../../../models/Entities/Contracts/ContractAddendum";
import moment from "moment";
import { useTranslation } from "react-i18next";

import {
    Document24Regular,
    Edit16Regular,
    Delete16Regular,
    Handshake24Regular,
    Prohibited16Regular,
    CheckmarkCircle16Regular,
    LockClosed16Regular
} from "@fluentui/react-icons";

// styles
import './Addendums.Styles.css';
import { useState } from "react";

import * as ComponentFunctions from './Addendums.Functions';
import { ContractAddendumType } from "../../../../models/Entities/Contracts/NewContractAddendum";



export interface AddendumItemProps {
    Addendum: ContractAddendum;
    SelectedAddendum: ContractAddendum | undefined;
    OnSelect: (addendum: ContractAddendum) => void;
    OnEdit?: (addendum: ContractAddendum) => void;
    OnDelete?: (addendum: ContractAddendum) => void;
    /** Habilita o deshabilita la adenda (Contracts 008). Si no se pasa, no se muestra la acción. */
    OnToggleEnabled?: (addendum: ContractAddendum) => void;
    DeletingId?: number | undefined;
}

/** Deshabilitada sólo cuando el backend lo dice: las respuestas viejas no traen `isEnable` y cuentan como habilitadas. */
export const isAddendumDisabled = (addendum: Pick<ContractAddendum, 'isEnable'>): boolean => addendum.isEnable === false;

export const AddendumItemComponent = (props: AddendumItemProps) => {
    const [hovered, setHovered] = useState(false);
    const { t } = useTranslation();

    const disabled = isAddendumDisabled(props.Addendum);
    const blocked = props.Addendum.isBlock === true;
    const changingEnabled = props.Addendum.isChangingEnabled === true;

    const formatDate = (value: Date | undefined) => (value ? moment(value).format("DD/MM/YYYY") : t('addendumItem.notInformed'));

    const AddendumExpiricy = (
        <span className="AddendumSecondary">
            {(props.Addendum?.contractAddendumExpiryStartDate || props.Addendum?.contractAddendumExpiryEndDate) ? (
                <Text style={{ fontSize: '12px' }}>{formatDate(props.Addendum?.contractAddendumExpiryStartDate)} - {formatDate(props.Addendum?.contractAddendumExpiryEndDate)}</Text>
            ) : (
                <Text style={{ fontSize: '12px' }}>{t('addendumItem.validityNotDefined')}</Text>
            )}
            {disabled && (
                <>
                    <br />
                    <span className="AddendumStateLabel AddendumStateLabel--disabled" data-testid={`addendum-disabled-${props.Addendum.id}`}>
                        {t('addendumItem.disabled')}
                    </span>
                </>
            )}
            {blocked && (
                <>
                    <br />
                    <span className="AddendumStateLabel" data-testid={`addendum-blocked-${props.Addendum.id}`}>
                        <LockClosed16Regular style={{ verticalAlign: 'text-bottom' }} /> {t('addendumItem.blocked')}
                    </span>
                </>
            )}
        </span>
    );

    const isCurrent = ComponentFunctions.isCurrentAddendum(props.Addendum);
    const isExpired = ComponentFunctions.isExpiredAddendum(props.Addendum);
    const isPending = !isCurrent && !isExpired;

    const statusClass = isPending
        ? 'addendum-item-pending'
        : props.Addendum.isActive
            ? 'addendum-item-active'
            : 'addendum-item-inactive';

    const stateBackgroundClass = isCurrent
        ? 'CurrentAddendum'
        : isExpired
            ? 'ExpiredAddendum'
            : isPending
                ? 'PendingAddendum'
                : '';

    const selectedClass = props.Addendum.id === props.SelectedAddendum?.id ? 'Active' : '';
    const disabledClass = disabled ? 'addendum-item-disabled' : '';
    const typeClass = props.Addendum.contractAddendumType === ContractAddendumType.NegociationInstance ? 'NegiciationAddendum' : '';

    const toggleTitle = disabled ? t('addendumItem.enable') : t('addendumItem.disable');

    return (
        <CompoundButton
            id={`addendum-${props.Addendum.id}`}
            className={`${statusClass} ${stateBackgroundClass} ${selectedClass} ${disabledClass} AddendumButton ${typeClass}`.replace(/\s+/g, ' ').trim()}
            key={props.Addendum.id}
            appearance={props.Addendum.id === props.SelectedAddendum?.id ? "primary" : "secondary"}
            icon={props.Addendum.isGettingContractStages ? <Spinner appearance="inverted" size="small" /> : props.Addendum.contractAddendumType === ContractAddendumType.NegociationInstance ? <Handshake24Regular /> : <Document24Regular />}
            secondaryContent={AddendumExpiricy}
            onClick={() => props.OnSelect(props.Addendum)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            data-testid={`addendum-item-${props.Addendum.id}`}
            data-disabled={disabled ? 'true' : undefined}
        >
            {props.Addendum.contractAddendumName}
            {hovered && (
                <div className="AddendumFloatingActions">
                    <Button
                        appearance="subtle"
                        icon={<Edit16Regular />}
                        onClick={(e) => { e.stopPropagation(); props.OnEdit?.(props.Addendum); }}
                        title={t('addendumItem.edit')}
                        aria-label={t('addendumItem.edit')}
                    />
                    {props.OnToggleEnabled && (
                        <Button
                            appearance="subtle"
                            icon={changingEnabled ? <Spinner size="extra-tiny" /> : disabled ? <CheckmarkCircle16Regular /> : <Prohibited16Regular />}
                            onClick={(e) => { e.stopPropagation(); props.OnToggleEnabled?.(props.Addendum); }}
                            disabled={changingEnabled}
                            title={toggleTitle}
                            aria-label={toggleTitle}
                            aria-pressed={disabled}
                            data-testid={`addendum-toggle-enabled-${props.Addendum.id}`}
                        />
                    )}
                    <Button
                        appearance="subtle"
                        icon={props.DeletingId === props.Addendum.id ? <Spinner size="extra-tiny" /> : <Delete16Regular />}
                        onClick={(e) => { e.stopPropagation(); props.OnDelete?.(props.Addendum); }}
                        title={t('addendumItem.delete')}
                        aria-label={t('addendumItem.delete')}
                    />
                </div>
            )}
        </CompoundButton>
    )
}
